import { Toast } from '@byecode/ui'
import { ErrorFallback } from '@lighthouse/block'
import type { AppendParams } from '@lighthouse/shared'
import { DataSourceProvider, useAtomAction } from '@lighthouse/shared'
import { getDefaultStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { fetchDataSourceAtom, fetchDataSourceUserAtom, setCurrentDataSourceIdAtom } from '@/atoms/dataSource/action'
import { filterCacheAtomFamily, sortsCacheAtomFamily } from '@/atoms/storage/state'
import DataSourceList from '@/components/DataSourceList'
import { useCurrentAppId } from '@/hooks/useApplication'
import {
    useCurrentDataSourceId,
    useDataSourceDepartments,
    useDataSourceEnvId,
    useDataSourceList,
    useDataSourceRoles,
    useDataSourceUsers
} from '@/hooks/useDataSource'
import * as srv from '@/services'

import * as SC from './styles'

const DATA_SOURCE_HISTORY_LIMIT = 20

const getDataSourceHistoryStorageKey = (appId: string, envId: string) => `builder-data-source-history:${appId}:${envId}`

const readDataSourceHistory = (appId: string, envId: string) => {
    if (!appId || !envId || typeof window === 'undefined') {
        return []
    }
    try {
        const rawValue = window.localStorage.getItem(getDataSourceHistoryStorageKey(appId, envId))
        if (!rawValue) {
            return []
        }
        const parsedValue = JSON.parse(rawValue)
        return Array.isArray(parsedValue) ? parsedValue.filter(item => typeof item === 'string') : []
    } catch {
        return []
    }
}

const writeDataSourceHistory = (appId: string, envId: string, ids: string[]) => {
    if (!appId || !envId || typeof window === 'undefined') {
        return
    }
    window.localStorage.setItem(getDataSourceHistoryStorageKey(appId, envId), JSON.stringify(ids))
}

const DataSource: React.FC = () => {
    const store = getDefaultStore()
    const currentAppId = useCurrentAppId()
    const envId = useDataSourceEnvId()
    const dataSourceList = useDataSourceList(currentAppId, envId)
    const users = useDataSourceUsers()
    const roles = useDataSourceRoles()
    const departments = useDataSourceDepartments()
    const { run: fetchDataSourceUser } = useAtomAction(fetchDataSourceUserAtom)
    const { run: setCurrentDataSourceId } = useAtomAction(setCurrentDataSourceIdAtom)
    const navigate = useNavigate()
    const { hash } = useLocation()
    const [visibleJoinConfigure, setVisibleJoinConfigure] = useState(false)
    const [scrollToDataSourceId, setScrollToDataSourceId] = useState<string | undefined>(undefined)
    const [historyIds, setHistoryIds] = useState<string[]>([])
    const skipHistoryReorderRef = useRef(false)
    const activeId = useCurrentDataSourceId()

    const { run: fetchDataSource, loading } = useAtomAction(fetchDataSourceAtom)

    const handleNavigateToEr = useCallback(() => {
        setCurrentDataSourceId({ dsId: '' })
        navigate({ pathname: `/${currentAppId}/dataSource/er` })
    }, [currentAppId, navigate, setCurrentDataSourceId])

    const handleSelectDataSource = useCallback(
        (dsId: string) => {
            if (dsId) {
                setCurrentDataSourceId({ dsId })
                navigate({ pathname: `/${currentAppId}/dataSource/${dsId}` })
                return
            }
            navigate({ pathname: `/${currentAppId}/dataSource` })
        },
        [currentAppId, navigate, setCurrentDataSourceId]
    )

    const handleAppendData = useCallback(
        async (params: AppendParams) => {
            const { dsId } = params.sheetDto
            const isAppend = await srv.appendDataToDataSource(params)
            isAppend && Toast.success('成功追加导入到数据源')
            if (dsId === activeId && isAppend) {
                const filterCache = store.get(filterCacheAtomFamily({ appId: currentAppId, envId, id: dsId }))
                const sortsCache = store.get(sortsCacheAtomFamily({ appId: currentAppId, envId, id: dsId }))
                fetchDataSource({
                    envId,
                    dsId: activeId,
                    pagination: { currentPage: 1, pageSize: 100 },
                    filter: filterCache,
                    sorts: sortsCache
                })
            }
            return isAppend
        },
        [activeId, currentAppId, envId, fetchDataSource, store]
    )

    useEffect(() => {
        fetchDataSourceUser()
    }, [fetchDataSourceUser, envId])

    useEffect(() => {
        setHistoryIds(readDataSourceHistory(currentAppId, envId))
    }, [currentAppId, envId])

    useEffect(() => {
        if (!activeId) {
            return
        }
        if (skipHistoryReorderRef.current) {
            skipHistoryReorderRef.current = false
            return
        }
        setHistoryIds(prev => {
            const nextIds = [activeId, ...prev.filter(id => id !== activeId)].slice(0, DATA_SOURCE_HISTORY_LIMIT)
            writeDataSourceHistory(currentAppId, envId, nextIds)
            return nextIds
        })
    }, [activeId, currentAppId, envId])

    useEffect(() => {
        if (!historyIds.length) {
            return
        }
        const validIdSet = new Set(dataSourceList.map(item => item.id))
        const nextIds = historyIds.filter(id => validIdSet.has(id))
        if (nextIds.length !== historyIds.length) {
            setHistoryIds(nextIds)
            writeDataSourceHistory(currentAppId, envId, nextIds)
        }
    }, [currentAppId, dataSourceList, envId, historyIds])

    // 根据 URL hash 设置数据源列表滚动目标（由 Item 在渲染到目标条目时执行滚动）
    useEffect(() => {
        if (!hash) {
            setScrollToDataSourceId(undefined)
            return
        }
        const targetId = hash.startsWith('#') ? hash.slice(1) : hash
        setScrollToDataSourceId(targetId || undefined)
    }, [hash])

    const handleVisibleJoinConfigureChange = useCallback((visible: boolean) => {
        setVisibleJoinConfigure(visible)
    }, [])

    const historyDataSources = useMemo(() => {
        const dataSourceMap = new Map(dataSourceList.map(item => [item.id, item] as const))
        return historyIds
            .map(id => {
                const dataSource = dataSourceMap.get(id)
                if (!dataSource) {
                    return undefined
                }
                return {
                    id,
                    name: dataSource.name
                }
            })
            .filter(Boolean) as { id: string; name: string }[]
    }, [dataSourceList, historyIds])

    const handleRemoveHistory = useCallback(
        (dsId: string) => {
            setHistoryIds(prev => {
                const nextIds = prev.filter(id => id !== dsId)
                writeDataSourceHistory(currentAppId, envId, nextIds)
                return nextIds
            })
        },
        [currentAppId, envId]
    )

    const handleSelectHistoryDataSource = useCallback(
        (dsId: string) => {
            skipHistoryReorderRef.current = true
            handleSelectDataSource(dsId)
        },
        [handleSelectDataSource]
    )

    const dataSourceContextValue = useMemo(
        () => ({
            personOptions: users,
            roleOptions: roles,
            departmentOptions: departments,
            visibleJoinConfigure,
            onVisibleJoinConfigureChange: handleVisibleJoinConfigureChange,
            scrollToDataSourceId,
            setScrollToDataSourceId
        }),
        [departments, handleVisibleJoinConfigureChange, roles, scrollToDataSourceId, users, visibleJoinConfigure]
    )

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DataSourceProvider value={dataSourceContextValue}>
                <SC.Container>
                    <SC.Content>
                        {activeId && (
                            <DataSourceList
                                activeId={activeId}
                                onNavigateToEr={handleNavigateToEr}
                                onSelectDataSource={handleSelectDataSource}
                                onDataAppended={handleAppendData}
                            />
                        )}
                        <SC.Main>
                            {!!historyDataSources.length && (
                                <SC.HistoryBar>
                                    <SC.HistoryScroll>
                                        {historyDataSources.map(item => (
                                            <SC.HistoryItem
                                                key={item.id}
                                                $active={item.id === activeId}
                                                onClick={() => handleSelectHistoryDataSource(item.id)}
                                            >
                                                <SC.HistoryName>{item.name}</SC.HistoryName>
                                                <SC.HistoryClose
                                                    type="Close"
                                                    size={12}
                                                    onClick={event => {
                                                        event.stopPropagation()
                                                        handleRemoveHistory(item.id)
                                                    }}
                                                />
                                            </SC.HistoryItem>
                                        ))}
                                    </SC.HistoryScroll>
                                </SC.HistoryBar>
                            )}
                            <SC.OutletWrapper>{activeId !== undefined && <Outlet />}</SC.OutletWrapper>
                        </SC.Main>
                    </SC.Content>
                </SC.Container>
            </DataSourceProvider>
        </ErrorBoundary>
    )
}

export default DataSource
